import React, { useMemo, useState } from 'react';
import { useTabs, Tab, TabItem } from './TabsContext';
import { useCategories } from './CategoriesContext';
import { useModifiers } from './ModifiersContext';
import { useTakeawayPackages } from './TakeawayPackagesContext';
import { useStock } from './StockContext';
import { Sidebar } from './Sidebar';
import { useBranches } from './BranchesContext';
import { useBranchMenu } from './MenusContext';
import { Menu as MenuIcon, Lock, Plus, Users, Clock, ChevronLeft, Send, Receipt, Search, Check, AlertTriangle, X, Edit2, Trash2, Package, Bell, RefreshCw } from 'lucide-react';
export function WaiterPage() {
  const {
    branchId
  } = useBranches();
  const branchMenu = useBranchMenu(branchId);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {
    tabs,
    openTab,
    addItemToTab,
    updateTabItem,
    removeTabItem,
    updateTab,
    sendToKitchen,
    closeTab,
    markDelivered,
    refireItem,
    remakeItem,
    reorderItem
  } = useTabs();
  const [remakingItemId, setRemakingItemId] = useState<string | null>(null);
  const [remakeReasonDraft, setRemakeReasonDraft] = useState('');
  const [billConfirmTabId, setBillConfirmTabId] = useState<string | null>(null);
  const {
    categories
  } = useCategories();
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isNewTabModalOpen, setIsNewTabModalOpen] = useState(false);
  const [newTableNum, setNewTableNum] = useState('');
  const [newGuestCount, setNewGuestCount] = useState('2');
  const [newOrderType, setNewOrderType] = useState<'dine-in' | 'takeout'>('dine-in');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('cat_savory');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingItem, setEditingItem] = useState<TabItem | null>(null);
  const [splitCount, setSplitCount] = useState(1);
  const [splitMode, setSplitMode] = useState<'single' | 'even' | 'byItem'>('single');
  const [itemAssignments, setItemAssignments] = useState<Record<string, number>>({});
  const {
    modifiers
  } = useModifiers();
  const {
    packages
  } = useTakeawayPackages();
  const {
    isOutOfStock
  } = useStock();
  const activeTab = useMemo(() => tabs.find(t => t.id === activeTabId), [tabs, activeTabId]);
  const openTabs = useMemo(() => tabs.filter(t => t.status === 'open'), [tabs]);
  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length >= 4) {
      setIsAuthenticated(true);
    }
  };
  const handleOpenTab = (e: React.FormEvent) => {
    e.preventDefault();
    if (newOrderType === 'dine-in' && !newTableNum) return;
    if (newOrderType === 'takeout' && !newCustomerName.trim()) return;
    const id = openTab(newOrderType === 'takeout' ? '' : newTableNum, newOrderType === 'takeout' ? 1 : parseInt(newGuestCount) || 1, pin, newOrderType, newOrderType === 'takeout' ? newCustomerName.trim() : undefined);
    setIsNewTabModalOpen(false);
    setNewTableNum('');
    setNewGuestCount('2');
    setNewCustomerName('');
    setNewOrderType('dine-in');
    setActiveTabId(id);
  };
  const formatTimeElapsed = (openedAt: number) => {
    const mins = Math.floor((Date.now() - openedAt) / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return `${hrs}h ${rem}m`;
  };
  const calculateTabTotal = (tab: Tab) => {
    const itemsTotal = tab.items.reduce((sum, item) => {
      // Complimentary remakes don't contribute to the customer's bill.
      if (item.complimentary) return sum;
      const modsPrice = item.modifiers?.reduce((mSum, m) => mSum + m.price, 0) || 0;
      return sum + (item.unitPrice + modsPrice) * item.quantity;
    }, 0);
    const pkg = packages.find(p => p.id === tab.packagingId);
    return itemsTotal + (pkg ? pkg.price : 0);
  };
  const filteredMenu = useMemo(() => {
    let items = branchMenu;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i => i.name.toLowerCase().includes(q));
    } else {
      const selectedCategory = categories.find(c => c.id === selectedCategoryId);
      if (selectedCategory) {
        items = items.filter(i => i.category === selectedCategory.name);
      }
    }
    return items;
  }, [branchMenu, searchQuery, selectedCategoryId, categories]);
  if (!isAuthenticated) {
    return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-3637">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-3638" />
        <main className="flex-1 flex items-center justify-center p-4" data-id="element-3639">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 max-w-sm w-full text-center" data-id="element-3640">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3641">
              <Lock size={28} data-id="element-3642" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3643">
              Waiter Login
            </h1>
            <p className="text-sm text-gray-500 mb-8" data-id="element-3644">
              Enter your 4-digit PIN to access the terminal.
            </p>

            <form onSubmit={handlePinSubmit} data-id="element-3645">
              <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="••••" className="w-full text-center text-3xl tracking-[0.5em] py-4 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 mb-6" maxLength={4} autoFocus data-id="element-3646" />
              <button type="submit" disabled={pin.length < 4} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold hover:bg-[#1e293b] transition-colors disabled:opacity-50" data-id="element-3647">
                Unlock Terminal
              </button>
            </form>
          </div>
        </main>
      </div>;
  }
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-3648">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-3649">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-3650">
          <MenuIcon size={24} data-id="element-3651" />
        </button>
        <span className="font-semibold" data-id="element-3652">Waiter Terminal</span>
        <div className="w-6" data-id="element-3653" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-3654" />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative" data-id="element-3655">
        {activeTab /* Tab Detail View (POS) */ ? <div className="flex-1 flex flex-col md:flex-row overflow-hidden" data-id="element-3656">
            {/* Left: Menu Browser */}
            <div className="flex-1 flex flex-col bg-[#f9fafb] overflow-hidden" data-id="element-3657">
              <div className="p-4 bg-white border-b border-gray-200 flex items-center gap-4" data-id="element-3658">
                <button onClick={() => setActiveTabId(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600" data-id="element-3659">
                  <ChevronLeft size={20} data-id="element-3660" />
                </button>
                <div className="flex-1 relative" data-id="element-3661">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3662" />
                  <input type="text" placeholder="Search menu..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500" data-id="element-3663" />
                </div>
              </div>

              {!searchQuery && <div className="flex overflow-x-auto p-4 gap-2 border-b border-gray-200 bg-white flex-shrink-0 scrollbar-hide" data-id="element-3664">
                  {categories.map(cat => <button key={cat.id} onClick={() => setSelectedCategoryId(cat.id)} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCategoryId === cat.id ? 'bg-[#0f172a] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3665">
                      {cat.name}
                    </button>)}
                </div>}

              <div className="flex-1 overflow-y-auto p-4" data-id="element-3666">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3667">
                  {filteredMenu.map(item => {
                const outOfStock = isOutOfStock(item.name);
                return <button key={item.id} onClick={() => addItemToTab(activeTab.id, {
                  name: item.name,
                  quantity: 1,
                  unitPrice: item.price
                })} disabled={outOfStock} className={`p-4 rounded-xl border shadow-sm text-left flex flex-col h-full transition-all ${outOfStock ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-orange-400 hover:shadow-md'}`} data-id="element-3668">
                        <div className="font-bold text-gray-900 mb-1" data-id="element-3669">
                          {item.name}
                        </div>
                        <div className="text-orange-600 font-semibold text-sm mt-auto flex items-center justify-between w-full" data-id="element-3670">
                          <span data-id="element-3671">ETB {item.price}</span>
                          {outOfStock && <span className="text-[10px] uppercase tracking-wider text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded font-bold" data-id="element-3672">
                              Out
                            </span>}
                        </div>
                      </button>;
              })}
                </div>
              </div>
            </div>

            {/* Right: Current Tab */}
            <div className="w-full md:w-96 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 shadow-xl z-10" data-id="element-3673">
              <div className="p-4 border-b border-gray-200 bg-[#0f172a] text-white" data-id="element-3674">
                <div className="flex items-center justify-between mb-1" data-id="element-3675">
                  <h2 className="text-xl font-bold" data-id="element-3676">
                    {activeTab.orderType === 'takeout' ? activeTab.customerName || 'Takeout' : `Table ${activeTab.tableNumber}`}
                  </h2>
                  {activeTab.orderType === 'dine-in' && <span className="text-sm font-medium bg-white/20 px-2 py-1 rounded-md" data-id="element-3677">
                      {activeTab.guestCount} Guests
                    </span>}
                </div>
                <div className="text-sm text-gray-400 flex items-center justify-between" data-id="element-3678">
                  <div className="flex items-center gap-1" data-id="element-3679">
                    <Clock size={12} data-id="element-3680" />
                    Opened {formatTimeElapsed(activeTab.openedAt)} ago
                  </div>
                  <span className="uppercase text-[10px] font-bold tracking-wider text-orange-400" data-id="element-3681">
                    {activeTab.orderType}
                  </span>
                </div>
                {activeTab.orderType === 'takeout' && <div className="mt-3 pt-3 border-t border-white/10" data-id="element-3682">
                    <label className="block text-xs font-medium text-gray-300 mb-1" data-id="element-3683">
                      Packaging
                    </label>
                    <div className="relative" data-id="element-3684">
                      <select value={activeTab.packagingId || ''} onChange={e => updateTab(activeTab.id, {
                  packagingId: e.target.value || undefined
                })} className="w-full appearance-none bg-white/10 border border-white/20 text-white text-sm rounded-lg pl-3 pr-8 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-3685">
                        <option value="" className="text-gray-900" data-id="element-3686">
                          No packaging
                        </option>
                        {packages.filter(p => p.active).map(p => <option key={p.id} value={p.id} className="text-gray-900" data-id="element-3687">
                              {p.name} (+ETB {p.price})
                            </option>)}
                      </select>
                      <Package size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3688" />
                    </div>
                  </div>}
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-3689">
                {/* Pending — not yet sent to kitchen */}
                {activeTab.items.filter(i => !i.sentToKitchenAt).length > 0 && <div data-id="element-3690">
                    <h3 className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-3" data-id="element-3691">
                      Pending — not yet sent
                    </h3>
                    <div className="space-y-3" data-id="element-3692">
                      {activeTab.items.filter(i => !i.sentToKitchenAt).map(item => <div key={item.id} className="group flex justify-between items-start p-2 -mx-2 rounded-lg hover:bg-orange-50 transition-colors" data-id="element-3693">
                            <div className="flex-1 pr-4" data-id="element-3694">
                              <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap" data-id="element-3695">
                                <span className="text-orange-600" data-id="element-3696">
                                  {item.quantity}x
                                </span>
                                {item.name}
                                {item.complimentary && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" data-id="element-3697">
                                    <RefreshCw size={9} data-id="element-3698" />
                                    Remake · Comp
                                  </span>}
                                <button onClick={() => setEditingItem(item)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-orange-600 transition-all" title="Edit item" data-id="element-3699">
                                  <Edit2 size={14} data-id="element-3700" />
                                </button>
                                <button onClick={() => removeTabItem(activeTab.id, item.id)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-rose-600 transition-all" title="Remove from tab" data-id="element-3701">
                                  <Trash2 size={14} data-id="element-3702" />
                                </button>
                              </div>
                              {item.complimentary && item.remakeReason && <div className="text-[11px] text-rose-600 italic mt-0.5 ml-6" data-id="element-3703">
                                  Reason: {item.remakeReason}
                                </div>}
                              {item.modifiers && item.modifiers.length > 0 && <div className="text-xs text-gray-500 mt-0.5 ml-6" data-id="element-3704">
                                  {item.modifiers.map(m => `+ ${m.name}`).join(', ')}
                                </div>}
                              {item.notes && <div className="text-xs text-gray-500 italic mt-0.5 ml-6" data-id="element-3705">
                                  "{item.notes}"
                                </div>}
                            </div>
                            <div className="font-semibold text-gray-900 pt-0.5 tabular-nums text-right" data-id="element-3706">
                              {item.complimentary ? <span className="text-rose-600" data-id="element-3707">
                                  ETB 0
                                  <div className="text-[10px] text-gray-400 line-through font-normal" data-id="element-3708">
                                    {(item.unitPrice + (item.modifiers?.reduce((s, m) => s + m.price, 0) || 0)) * item.quantity}
                                  </div>
                                </span> : (item.unitPrice + (item.modifiers?.reduce((s, m) => s + m.price, 0) || 0)) * item.quantity}
                            </div>
                          </div>)}
                    </div>
                  </div>}

                {/* Ready for pickup — kitchen flagged it ready, waiter hasn't grabbed it yet */}
                {activeTab.items.filter(i => i.sentToKitchenAt && i.ready && !i.deliveredAt).length > 0 && <div className="-mx-2 px-2 py-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-200" data-id="element-3709">
                    <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-3710">
                      <Bell size={12} data-id="element-3711" />
                      Ready for pickup
                    </h3>
                    <div className="space-y-3" data-id="element-3712">
                      {activeTab.items.filter(i => i.sentToKitchenAt && i.ready && !i.deliveredAt).map(item => <div key={item.id} className="space-y-1.5" data-id="element-3713">
                            <div className="flex justify-between items-start" data-id="element-3714">
                              <div className="font-semibold text-gray-900" data-id="element-3715">
                                <span className="text-emerald-700 mr-2" data-id="element-3716">
                                  {item.quantity}x
                                </span>
                                {item.name}
                              </div>
                              <div className="font-semibold text-gray-900" data-id="element-3717">
                                {(item.unitPrice + (item.modifiers?.reduce((s, m) => s + m.price, 0) || 0)) * item.quantity}
                              </div>
                            </div>
                            {item.modifiers && item.modifiers.length > 0 && <div className="text-xs text-gray-500 ml-6" data-id="element-3718">
                                {item.modifiers.map(m => `+ ${m.name}`).join(', ')}
                              </div>}
                            {item.notes && <div className="text-xs text-gray-500 italic ml-6" data-id="element-3719">
                                "{item.notes}"
                              </div>}
                            {item.readyAt && <div className="text-[10px] text-emerald-700 ml-6" data-id="element-3720">
                                Ready since{' '}
                                {new Date(item.readyAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                              </div>}
                            <button type="button" onClick={() => markDelivered(activeTab.id, item.id, pin)} className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors" data-id="element-3721">
                              <Check size={12} data-id="element-3722" />
                              Mark delivered
                            </button>
                          </div>)}
                    </div>
                  </div>}

                {/* Awaiting kitchen — sent but kitchen hasn't flagged ready yet */}
                {activeTab.items.filter(i => i.sentToKitchenAt && !i.ready && !i.deliveredAt).length > 0 && <div data-id="element-3723">
                    <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3" data-id="element-3724">
                      Cooking
                    </h3>
                    <div className="space-y-3" data-id="element-3725">
                      {activeTab.items.filter(i => i.sentToKitchenAt && !i.ready && !i.deliveredAt).map(item => <div key={item.id} className="space-y-1.5" data-id="element-3726">
                            <div className="flex justify-between items-start" data-id="element-3727">
                              <div className="font-semibold text-gray-900" data-id="element-3728">
                                <span className="text-amber-600 mr-2" data-id="element-3729">
                                  {item.quantity}x
                                </span>
                                {item.name}
                              </div>
                              <div className="font-semibold text-gray-900" data-id="element-3730">
                                {(item.unitPrice + (item.modifiers?.reduce((s, m) => s + m.price, 0) || 0)) * item.quantity}
                              </div>
                            </div>
                            {item.modifiers && item.modifiers.length > 0 && <div className="text-xs text-gray-500 ml-6" data-id="element-3731">
                                {item.modifiers.map(m => `+ ${m.name}`).join(', ')}
                              </div>}
                            {item.notes && <div className="text-xs text-gray-500 italic ml-6" data-id="element-3732">
                                "{item.notes}"
                              </div>}
                          </div>)}
                    </div>
                  </div>}

                {/* Delivered — handed to the table */}
                {activeTab.items.filter(i => i.deliveredAt).length > 0 && <div data-id="element-3733">
                    <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3" data-id="element-3734">
                      Delivered
                    </h3>
                    <div className="space-y-3" data-id="element-3735">
                      {activeTab.items.filter(i => i.deliveredAt).map(item => <div key={item.id} className="opacity-80 space-y-0.5" data-id="element-3736">
                            <div className="flex justify-between items-start" data-id="element-3737">
                              <div className="font-medium text-gray-900" data-id="element-3738">
                                <span className="text-gray-500 mr-2" data-id="element-3739">
                                  {item.quantity}x
                                </span>
                                {item.name}
                              </div>
                              <div className="font-medium text-gray-900" data-id="element-3740">
                                {(item.unitPrice + (item.modifiers?.reduce((s, m) => s + m.price, 0) || 0)) * item.quantity}
                              </div>
                            </div>
                            {item.modifiers && item.modifiers.length > 0 && <div className="text-[11px] text-gray-500 ml-6" data-id="element-3741">
                                {item.modifiers.map(m => `+ ${m.name}`).join(', ')}
                              </div>}
                            <div className="text-[11px] text-gray-500 mt-1" data-id="element-3742">
                              Delivered at{' '}
                              {new Date(item.deliveredAt as number).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}{' '}
                              by waiter {item.deliveredBy}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5" data-id="element-3743">
                              <button type="button" onClick={() => setRemakingItemId(item.id)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded transition-colors" title="Quality issue — comp the customer" data-id="element-3744">
                                <RefreshCw size={11} data-id="element-3745" />
                                Remake (no charge)
                              </button>
                              <button type="button" onClick={() => reorderItem(activeTab.id, item.id)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-2 py-1 rounded transition-colors" title="Customer wants another — charged" data-id="element-3746">
                                <Plus size={11} data-id="element-3747" />
                                Order again
                              </button>
                            </div>
                            {remakingItemId === item.id && <div className="mt-2 p-2 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2" data-id="element-3748">
                                <input type="text" autoFocus value={remakeReasonDraft} onChange={e => setRemakeReasonDraft(e.target.value)} placeholder="Reason (optional) — e.g. too cold, wrong cook" className="flex-1 bg-white border border-rose-200 rounded px-2 py-1 text-[12px] outline-none focus:border-rose-400" data-id="element-3749" />
                                <button type="button" onClick={() => {
                      remakeItem(activeTab.id, item.id, remakeReasonDraft.trim() || undefined);
                      setRemakingItemId(null);
                      setRemakeReasonDraft('');
                    }} className="px-2.5 py-1 rounded bg-rose-600 text-white text-[11px] font-semibold hover:bg-rose-700 transition-colors" data-id="element-3750">
                                  Send remake
                                </button>
                                <button type="button" onClick={() => {
                      setRemakingItemId(null);
                      setRemakeReasonDraft('');
                    }} className="text-rose-600 hover:underline text-[11px]" data-id="element-3751">
                                  Cancel
                                </button>
                              </div>}
                          </div>)}
                    </div>
                  </div>}

                {activeTab.items.length === 0 && <div className="text-center text-gray-400 py-10" data-id="element-3752">
                    Tab is empty. Add items from the menu.
                  </div>}
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50" data-id="element-3753">
                <div className="flex justify-between items-center mb-4" data-id="element-3754">
                  <span className="text-gray-600 font-medium" data-id="element-3755">Total</span>
                  <span className="text-2xl font-bold text-gray-900" data-id="element-3756">
                    ETB {calculateTabTotal(activeTab)}
                  </span>
                </div>

                {(() => {
              const undeliveredCount = activeTab.items.filter(i => i.sentToKitchenAt && !i.deliveredAt).length;
              const pendingCount = activeTab.items.filter(i => !i.sentToKitchenAt).length;
              const hasUnaccounted = undeliveredCount + pendingCount > 0;
              const pkg = packages.find(p => p.id === activeTab.packagingId);
              return <>
                      <div className="grid grid-cols-2 gap-2" data-id="element-3757">
                        <button onClick={() => sendToKitchen(activeTab.id, pkg ? pkg.price : 0)} disabled={activeTab.items.filter(i => !i.sentToKitchenAt).length === 0} className="py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-3758">
                          <Send size={18} data-id="element-3759" />
                          Send
                        </button>
                        <button onClick={() => {
                    setSplitCount(1);
                    setBillConfirmTabId(activeTab.id);
                  }} disabled={activeTab.items.length === 0} className={`w-full py-3 rounded-xl bg-[#0f172a] text-white font-bold hover:bg-[#1e293b] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${hasUnaccounted ? 'ring-2 ring-amber-300' : ''}`} data-id="element-3760">
                          <Receipt size={18} data-id="element-3761" />
                          Bill
                        </button>
                      </div>
                      {hasUnaccounted && <div className="text-[10px] text-amber-700 text-center mt-1" data-id="element-3762">
                          {undeliveredCount + pendingCount} item
                          {undeliveredCount + pendingCount === 1 ? '' : 's'}{' '}
                          unaccounted
                        </div>}
                    </>;
            })()}
              </div>
            </div>

            {/* Item Editor Modal */}
            {editingItem && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-3763">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full flex flex-col max-h-[90vh]" data-id="element-3764">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 rounded-t-2xl" data-id="element-3765">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-3766">
                      Edit {editingItem.name}
                    </h2>
                    <button onClick={() => setEditingItem(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-3767">
                      <X size={20} data-id="element-3768" />
                    </button>
                  </div>

                  <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-3769">
                    <div data-id="element-3770">
                      <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-3771">
                        Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-3772">
                        <button type="button" onClick={() => setEditingItem({
                    ...editingItem,
                    quantity: Math.max(1, editingItem.quantity - 1)
                  })} className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 text-xl" data-id="element-3773">
                          -
                        </button>
                        <div className="flex-1 text-center text-2xl font-bold text-gray-900" data-id="element-3774">
                          {editingItem.quantity}
                        </div>
                        <button type="button" onClick={() => setEditingItem({
                    ...editingItem,
                    quantity: editingItem.quantity + 1
                  })} className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 text-xl" data-id="element-3775">
                          +
                        </button>
                      </div>
                    </div>

                    <div data-id="element-3776">
                      <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-3777">
                        Modifiers
                      </label>
                      <div className="space-y-2" data-id="element-3778">
                        {modifiers.filter(m => m.active).map(mod => {
                    const isSelected = editingItem.modifiers?.some(m => m.name === mod.name);
                    return <label key={mod.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-3779">
                                <div className="flex items-center gap-3" data-id="element-3780">
                                  <input type="checkbox" checked={isSelected} onChange={e => {
                          const currentMods = editingItem.modifiers || [];
                          if (e.target.checked) {
                            setEditingItem({
                              ...editingItem,
                              modifiers: [...currentMods, {
                                name: mod.name,
                                price: mod.price
                              }]
                            });
                          } else {
                            setEditingItem({
                              ...editingItem,
                              modifiers: currentMods.filter(m => m.name !== mod.name)
                            });
                          }
                        }} className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-id="element-3781" />
                                  <span className="font-medium text-gray-900" data-id="element-3782">
                                    {mod.name}
                                  </span>
                                </div>
                                <span className="text-gray-500 text-sm" data-id="element-3783">
                                  +ETB {mod.price}
                                </span>
                              </label>;
                  })}
                      </div>
                    </div>

                    <div data-id="element-3784">
                      <label className="block text-sm font-bold text-gray-700 mb-3" data-id="element-3785">
                        Special Instructions
                      </label>
                      <textarea value={editingItem.notes || ''} onChange={e => setEditingItem({
                  ...editingItem,
                  notes: e.target.value
                })} placeholder="e.g. Allergy, extra spicy..." className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 resize-none h-24" data-id="element-3786" />
                    </div>
                  </div>

                  <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl" data-id="element-3787">
                    <button onClick={() => {
                updateTabItem(activeTab.id, editingItem.id, {
                  quantity: editingItem.quantity,
                  modifiers: editingItem.modifiers,
                  notes: editingItem.notes
                });
                setEditingItem(null);
              }} className="w-full py-3.5 rounded-xl bg-[#0f172a] text-white font-bold hover:bg-[#1e293b] transition-colors" data-id="element-3788">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
          </div> /* Tabs Overview */ : <div className="p-4 md:p-8 h-full overflow-y-auto" data-id="element-3789">
            <div className="flex items-center justify-between mb-8" data-id="element-3790">
              <div data-id="element-3791">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-3792">Open Tabs</h1>
                <p className="text-gray-500 text-sm mt-1" data-id="element-3793">
                  {openTabs.length} active tables
                </p>
              </div>
              <div className="flex gap-3" data-id="element-3794">
                <button onClick={() => setIsAuthenticated(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors" data-id="element-3795">
                  Lock
                </button>
                <button onClick={() => setIsNewTabModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors" data-id="element-3796">
                  <Plus size={18} data-id="element-3797" />
                  New Tab
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-3798">
              {openTabs.map(tab => <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:border-orange-400 hover:shadow-md transition-all text-left flex flex-col" data-id="element-3799">
                  <div className="flex justify-between items-start mb-4" data-id="element-3800">
                    <div className="flex items-center gap-3 min-w-0" data-id="element-3801">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0 ${tab.orderType === 'takeout' ? 'bg-orange-100 text-orange-700' : 'bg-[#0f172a] text-white'}`} data-id="element-3802">
                        {tab.orderType === 'takeout' ? <Package size={20} data-id="element-3803" /> : tab.tableNumber}
                      </div>
                      {tab.orderType === 'takeout' && <div className="min-w-0" data-id="element-3804">
                          <div className="font-bold text-gray-900 truncate" data-id="element-3805">
                            {tab.customerName || 'Takeout'}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-orange-600 font-bold" data-id="element-3806">
                            Takeout
                          </div>
                        </div>}
                    </div>
                    <div className="text-right flex-shrink-0" data-id="element-3807">
                      <div className="text-lg font-bold text-gray-900" data-id="element-3808">
                        ETB {calculateTabTotal(tab)}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center justify-end gap-1 mt-1" data-id="element-3809">
                        <Clock size={10} data-id="element-3810" />
                        {formatTimeElapsed(tab.openedAt)}
                      </div>
                    </div>
                  </div>

                  {(() => {
              const readyCount = tab.items.filter(i => i.sentToKitchenAt && i.ready && !i.deliveredAt).length;
              return readyCount > 0 && <div className="mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold w-fit animate-pulse" data-id="element-3811">
                          <Bell size={11} data-id="element-3812" />
                          {readyCount} ready
                        </div>;
            })()}

                  <div className="mt-auto flex items-center gap-3 text-sm text-gray-600" data-id="element-3813">
                    {tab.orderType === 'dine-in' && <>
                        <div className="flex items-center gap-1.5" data-id="element-3814">
                          <Users size={14} data-id="element-3815" />
                          {tab.guestCount}
                        </div>
                        <div className="w-1 h-1 rounded-full bg-gray-300" data-id="element-3816" />
                      </>}
                    <div data-id="element-3817">{tab.items.length} items</div>
                    {tab.items.some(i => !i.sentToKitchenAt) && <>
                        <div className="w-1 h-1 rounded-full bg-gray-300" data-id="element-3818" />
                        <div className="text-orange-600 font-semibold text-xs" data-id="element-3819">
                          Unsent
                        </div>
                      </>}
                  </div>
                </button>)}

              {openTabs.length === 0 && <div className="col-span-full py-12 text-center border-2 border-dashed border-gray-200 rounded-2xl" data-id="element-3820">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400" data-id="element-3821">
                    <Receipt size={24} data-id="element-3822" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-3823">
                    No open tabs
                  </h3>
                  <p className="text-gray-500" data-id="element-3824">Tap New Tab to seat a table.</p>
                </div>}
            </div>
          </div>}

        {/* New Tab Modal */}
        {isNewTabModalOpen && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-3825">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" data-id="element-3826">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-3827">
                Open New Tab
              </h2>
              <form onSubmit={handleOpenTab} data-id="element-3828">
                <div className="space-y-4 mb-6" data-id="element-3829">
                  <div data-id="element-3830">
                    <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-3831">
                      Order Type
                    </label>
                    <div className="flex bg-gray-100 p-1 rounded-xl" data-id="element-3832">
                      <button type="button" onClick={() => setNewOrderType('dine-in')} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${newOrderType === 'dine-in' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3833">
                        Dine-in
                      </button>
                      <button type="button" onClick={() => setNewOrderType('takeout')} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${newOrderType === 'takeout' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3834">
                        Takeout
                      </button>
                    </div>
                  </div>

                  {newOrderType === 'dine-in' ? <>
                      <div data-id="element-3835">
                        <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-3836">
                          Table Number
                        </label>
                        <input type="text" value={newTableNum} onChange={e => setNewTableNum(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="e.g. 12" required autoFocus data-id="element-3837" />
                      </div>
                      <div data-id="element-3838">
                        <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-3839">
                          Number of Guests
                        </label>
                        <div className="flex items-center gap-3" data-id="element-3840">
                          <button type="button" onClick={() => setNewGuestCount(String(Math.max(1, parseInt(newGuestCount) - 1)))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3841">
                            -
                          </button>
                          <input type="number" value={newGuestCount} onChange={e => setNewGuestCount(e.target.value)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 text-center font-bold" min="1" required data-id="element-3842" />
                          <button type="button" onClick={() => setNewGuestCount(String(parseInt(newGuestCount) + 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3843">
                            +
                          </button>
                        </div>
                      </div>
                    </> : <div data-id="element-3844">
                      <label className="block text-sm font-bold text-gray-700 mb-1.5" data-id="element-3845">
                        Customer Name
                      </label>
                      <input type="text" value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200" placeholder="e.g. Selam" required autoFocus data-id="element-3846" />
                      <p className="mt-1.5 text-xs text-gray-500" data-id="element-3847">
                        Used to call the customer when the order is ready.
                      </p>
                    </div>}
                </div>
                <div className="flex gap-3" data-id="element-3848">
                  <button type="button" onClick={() => setIsNewTabModalOpen(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50" data-id="element-3849">
                    Cancel
                  </button>
                  <button type="submit" disabled={newOrderType === 'dine-in' ? !newTableNum : !newCustomerName.trim()} className="flex-1 py-3 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 disabled:opacity-50" data-id="element-3850">
                    Open Tab
                  </button>
                </div>
              </form>
            </div>
          </div>}

        {/* Bill Confirmation Modal */}
        {billConfirmTabId && <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-3851">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-3852">
              {/* Header */}
              <div className="px-6 pt-6 pb-5 border-b border-slate-100 flex-shrink-0" data-id="element-3853">
                <div className="flex items-center gap-3" data-id="element-3854">
                  <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-3855">
                    <Receipt size={18} data-id="element-3856" />
                  </div>
                  <div className="flex-1" data-id="element-3857">
                    <h2 className="text-lg font-bold text-slate-900" data-id="element-3858">
                      Settle bill
                    </h2>
                    {(() => {
                  const t = tabs.find(x => x.id === billConfirmTabId);
                  if (!t) return null;
                  return <p className="text-[13px] text-slate-500 mt-0.5" data-id="element-3859">
                          {t.orderType === 'takeout' ? t.customerName || 'Takeout' : `Table ${t.tableNumber}`}
                          <span className="text-slate-300 mx-1.5" data-id="element-3860">·</span>
                          {t.guestCount}{' '}
                          {t.guestCount === 1 ? 'guest' : 'guests'}
                        </p>;
                })()}
                  </div>
                </div>
              </div>

              {/* Body — keep the existing IIFE that renders the bill content */}
              <div className="px-6 py-5 overflow-y-auto flex-1" data-id="element-3861">
                {(() => {
              const tabToClose = tabs.find(t => t.id === billConfirmTabId);
              if (!tabToClose) return null;
              const undeliveredCount = tabToClose.items.filter(i => i.sentToKitchenAt && !i.deliveredAt).length;
              const pendingCount = tabToClose.items.filter(i => !i.sentToKitchenAt).length;
              const hasUnaccounted = undeliveredCount + pendingCount > 0;
              const total = calculateTabTotal(tabToClose);
              const deliveredItems = tabToClose.items.filter(i => i.deliveredAt);
              const lineTotal = (it: TabItem) => {
                // Complimentary remakes don't contribute to the customer's
                // bill, even though they appear as line items.
                if (it.complimentary) return 0;
                const modsTotal = it.modifiers?.reduce((s, m) => s + m.price, 0) || 0;
                return (it.unitPrice + modsTotal) * it.quantity;
              };
              // Compute per-guest totals for the byItem preview
              const sharedItems = deliveredItems.filter(it => itemAssignments[it.id] === undefined);
              const sharedTotal = sharedItems.reduce((s, it) => s + lineTotal(it), 0);
              const perGuestTotals: number[] = [];
              if (splitMode === 'byItem' && splitCount > 1) {
                const sharedPerGuest = sharedTotal / splitCount;
                for (let g = 0; g < splitCount; g++) {
                  const assigned = deliveredItems.filter(it => itemAssignments[it.id] === g).reduce((s, it) => s + lineTotal(it), 0);
                  const subtotal = assigned + sharedPerGuest;
                  const serviceFee = subtotal * 0.05;
                  const packagingShare = tabToClose.packagingId ? 0 : 0; // packaging fee will be split inside closeTab
                  perGuestTotals.push(subtotal + serviceFee + packagingShare);
                }
              }
              return <>
                      {hasUnaccounted && <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2" data-id="element-3862">
                          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3863" />
                          <p className="text-xs text-amber-800 flex-1" data-id="element-3864">
                            {undeliveredCount + pendingCount} item
                            {undeliveredCount + pendingCount === 1 ? '' : 's'}{' '}
                            {undeliveredCount + pendingCount === 1 ? "haven't" : "haven't"}{' '}
                            been delivered yet.
                          </p>
                        </div>}

                      <div className="bg-gray-50 p-4 rounded-xl mb-4" data-id="element-3865">
                        <div className="flex justify-between items-center" data-id="element-3866">
                          <span className="font-semibold text-gray-700" data-id="element-3867">
                            Total
                          </span>
                          <span className="text-2xl font-bold text-gray-900" data-id="element-3868">
                            ETB {total}
                          </span>
                        </div>
                      </div>

                      {/* Mode tabs */}
                      <div className="flex bg-gray-100 p-1 rounded-xl mb-4" data-id="element-3869">
                        {(['single', 'even', 'byItem'] as const).map(mode => <button key={mode} type="button" onClick={() => {
                    setSplitMode(mode);
                    if (mode === 'single') setSplitCount(1);
                    if (mode === 'even' && splitCount < 2) setSplitCount(2);
                    if (mode === 'byItem' && splitCount < 2) setSplitCount(2);
                  }} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${splitMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3870">
                            {mode === 'single' ? 'Single' : mode === 'even' ? 'Split evenly' : 'By item'}
                          </button>)}
                      </div>

                      {splitMode === 'even' && <div className="bg-gray-50 p-4 rounded-xl mb-4" data-id="element-3871">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-3872">
                            Split how many ways?
                          </label>
                          <div className="flex items-center gap-3" data-id="element-3873">
                            <button type="button" onClick={() => setSplitCount(Math.max(2, splitCount - 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3874">
                              -
                            </button>
                            <div className="flex-1 text-center font-bold text-gray-900 text-lg" data-id="element-3875">
                              {splitCount} ways
                            </div>
                            <button type="button" onClick={() => setSplitCount(Math.min(12, splitCount + 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3876">
                              +
                            </button>
                          </div>
                          <div className="text-center mt-3" data-id="element-3877">
                            <div className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-3878">
                              Each guest pays
                            </div>
                            <div className="text-3xl font-bold text-orange-600 mt-1" data-id="element-3879">
                              ETB {(total / splitCount).toFixed(2)}
                            </div>
                          </div>
                        </div>}

                      {splitMode === 'byItem' && <div className="space-y-4 mb-4" data-id="element-3880">
                          <div className="bg-gray-50 p-4 rounded-xl" data-id="element-3881">
                            <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-3882">
                              Number of guests
                            </label>
                            <div className="flex items-center gap-3" data-id="element-3883">
                              <button type="button" onClick={() => {
                        const next = Math.max(2, splitCount - 1);
                        setSplitCount(next);
                        // Clear assignments outside the new range
                        setItemAssignments(prev => {
                          const updated = {
                            ...prev
                          };
                          Object.keys(updated).forEach(k => {
                            if (updated[k] >= next) delete updated[k];
                          });
                          return updated;
                        });
                      }} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3884">
                                -
                              </button>
                              <div className="flex-1 text-center font-bold text-gray-900 text-lg" data-id="element-3885">
                                {splitCount} guests
                              </div>
                              <button type="button" onClick={() => setSplitCount(Math.min(8, splitCount + 1))} className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50" data-id="element-3886">
                                +
                              </button>
                            </div>
                          </div>

                          {deliveredItems.length === 0 ? <div className="text-center text-sm text-gray-500 py-6 border-2 border-dashed border-gray-200 rounded-xl" data-id="element-3887">
                              No delivered items to assign.
                            </div> : <div className="space-y-2" data-id="element-3888">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-3889">
                                Assign each item to a guest (unassigned items
                                are shared)
                              </div>
                              {deliveredItems.map(item => {
                      const assigned = itemAssignments[item.id];
                      return <div key={item.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 bg-white" data-id="element-3890">
                                    <div className="flex-1 min-w-0" data-id="element-3891">
                                      <div className="font-semibold text-gray-900 text-sm truncate" data-id="element-3892">
                                        {item.quantity}× {item.name}
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-3893">
                                        ETB {lineTotal(item)}
                                        {assigned === undefined && <span className="ml-2 text-orange-600 font-medium" data-id="element-3894">
                                            Shared
                                          </span>}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0" data-id="element-3895">
                                      {Array.from({
                            length: splitCount
                          }).map((_, g) => <button key={g} type="button" onClick={() => {
                            setItemAssignments(prev => {
                              const updated = {
                                ...prev
                              };
                              if (updated[item.id] === g) {
                                delete updated[item.id];
                              } else {
                                updated[item.id] = g;
                              }
                              return updated;
                            });
                          }} className={`w-8 h-8 rounded-full text-xs font-bold transition-colors ${assigned === g ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3896">
                                          {g + 1}
                                        </button>)}
                                    </div>
                                  </div>;
                    })}
                            </div>}

                          {deliveredItems.length > 0 && <div className="bg-gray-50 p-4 rounded-xl" data-id="element-3897">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2" data-id="element-3898">
                                Per-guest totals
                              </div>
                              <div className="space-y-1" data-id="element-3899">
                                {perGuestTotals.map((amt, i) => <div key={i} className="flex justify-between text-sm" data-id="element-3900">
                                    <span className="text-gray-700" data-id="element-3901">
                                      Guest {i + 1}
                                    </span>
                                    <span className="font-semibold text-gray-900" data-id="element-3902">
                                      ETB {amt.toFixed(2)}
                                    </span>
                                  </div>)}
                              </div>
                            </div>}
                        </div>}
                    </>;
            })()}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex-shrink-0 space-y-3" data-id="element-3903">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2.5" data-id="element-3904">
                  <Receipt size={14} className="text-slate-500 flex-shrink-0 mt-0.5" data-id="element-3905" />
                  <p className="text-[12px] leading-relaxed text-slate-600" data-id="element-3906">
                    <span className="font-semibold text-slate-800" data-id="element-3907">
                      Bring the printed bill to the cashier
                    </span>{' '}
                    with the customer's payment. The cashier picks the method
                    and verifies on their screen.
                  </p>
                </div>

                <div className="flex gap-2.5" data-id="element-3908">
                  <button onClick={() => {
                setBillConfirmTabId(null);
                setSplitMode('single');
                setSplitCount(1);
                setItemAssignments({});
              }} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors" data-id="element-3909">
                    Cancel
                  </button>
                  <button onClick={() => {
                closeTab(billConfirmTabId, splitMode === 'single' ? 1 : splitCount, splitMode === 'byItem' ? itemAssignments : undefined);
                setBillConfirmTabId(null);
                setActiveTabId(null);
                setSplitMode('single');
                setSplitCount(1);
                setItemAssignments({});
              }} className="flex-[1.5] py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors inline-flex items-center justify-center gap-2 shadow-sm" data-id="element-3910">
                    <Receipt size={15} data-id="element-3911" />
                    Print bill & send to cashier
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </main>
    </div>;
}